import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

export interface Notification {
    id: string
    userId: string
    workspaceId: string
    taskId: string
    projectId: string
    type: string
    watched: boolean
    watchedAt: any
    createdAt: any
    createdBy: string
    createdByName?: string | null
}

interface NotificationsState {
    byId: Record<string, Notification>
    allIds: string[]
    loading: boolean
    allByIdFull: Record<string, Notification>
    allIdsFull: string[]
    loadingFull: boolean
}

const initialState: NotificationsState = {
    byId: {},
    allIds: [],
    loading: false,
    allByIdFull: {},
    allIdsFull: [],
    loadingFull: false,
}

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        // Saga triggers
        startListening: (_state, _action: PayloadAction<{ userId: string; workspaceId: string }>) => {},
        stopListening: () => {},
        startListeningAll: (_state, _action: PayloadAction<{ userId: string; workspaceId: string }>) => {},
        stopListeningAll: () => {},
        markWatchedSaga: (_state, _action: PayloadAction<string>) => {},
        markProjectWatchedSaga: (_state, _action: PayloadAction<string>) => {},
        // Marks a single notification read by its own id. Needed for notifications whose
        // task/project is gone: the usual path (opening the task modal) can't run, so
        // without this an orphan notification stays unread forever.
        markNotificationWatchedSaga: (_state, _action: PayloadAction<string>) => {},
        markAllWatchedSaga: (_state, _action: PayloadAction<{ userId: string; workspaceId: string }>) => {},

        // State mutations
        setNotifications: (state, action: PayloadAction<Notification[]>) => {
            state.byId = {}
            state.allIds = []
            for (const notification of action.payload) {
                state.byId[notification.id] = notification
                state.allIds.push(notification.id)
            }
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload
        },
        clearNotifications: (state) => {
            state.byId = {}
            state.allIds = []
            state.loading = false
        },
        setAllNotifications: (state, action: PayloadAction<Notification[]>) => {
            state.allByIdFull = {}
            state.allIdsFull = []
            for (const notification of action.payload) {
                state.allByIdFull[notification.id] = notification
                state.allIdsFull.push(notification.id)
            }
        },
        setLoadingFull: (state, action: PayloadAction<boolean>) => {
            state.loadingFull = action.payload
        },
        clearAllNotifications: (state) => {
            state.allByIdFull = {}
            state.allIdsFull = []
            state.loadingFull = false
        },
    },
})

export const {
    startListening,
    stopListening,
    startListeningAll,
    stopListeningAll,
    markWatchedSaga,
    markProjectWatchedSaga,
    markNotificationWatchedSaga,
    markAllWatchedSaga,
    setNotifications,
    setLoading,
    clearNotifications,
    setAllNotifications,
    setLoadingFull,
    clearAllNotifications,
} = notificationsSlice.actions

export default notificationsSlice.reducer
